import React, { useMemo } from 'react';
import { ListBase, ListProps, useListContext } from 'react-admin';
import { FilterField } from '../Common/DataList';
import { NetOrcaTableListV1, TableListColumn } from '../Common/NetOrcaTableList';
import { getPOVResources } from '../Common/utils';
import ChargeChart from './ChargeChart';
import ChargeRow from './ChargeRow';
import ChargeSummary from './ChargeSummary';
import useChargeStatistics from './useChargeStatistics';
import { chargeTypeOptions, processedOptions } from './constants';
import './Charges.css';

const ChargeListView = () => {
    const { resource, filterValues } = useListContext();

    const pov = resource === 'consumer_charges' ? 'consumer' : 'serviceowner';
    const isOwner = pov === 'serviceowner';
    const { servicesResource, applicationsResource, serviceItemsResource, teamsResource } =
        getPOVResources(pov);

    const { points, totals, loading } = useChargeStatistics(resource, filterValues);

    /**
     * Everything the API's ChargeFilterSet accepts. Most are claimed by a
     * column header below; the rest wait in the panel.
     */
    const filterFields: FilterField[] = useMemo(
        () => [
            {
                // Parent of the cascade, so it comes first: a service owner
                // charges more than one team, so whose item was charged is a
                // question only they can ask.
                name: 'consumer_team_id',
                label: 'Consumer',
                type: 'select',
                resource: teamsResource,
                dataCy: 'team-selector',
                hidden: !isOwner,
            },
            {
                name: 'application_id',
                label: 'Application',
                type: 'select',
                resource: applicationsResource,
                dataCy: 'application-selector',
                // owners pick a team first; consumers have no team choice
                dependsOn: isOwner ? ['consumer_team_id'] : undefined,
            },
            {
                name: 'service_item_id',
                label: 'Service item',
                type: 'select',
                resource: serviceItemsResource,
                dataCy: 'service-item-selector',
                // an item belongs to an application, so that is chosen first —
                // the same chain the change instances list narrows by
                dependsOn: ['application_id'],
            },
            {
                name: 'service_id',
                label: 'Service',
                type: 'select',
                resource: servicesResource,
                dataCy: 'service-selector',
            },
            {
                name: 'charge_type',
                label: 'Charge type',
                type: 'select',
                options: chargeTypeOptions,
                dataCy: 'charge-type-filter',
            },
            {
                name: 'processed',
                label: 'Processed',
                type: 'select',
                options: processedOptions,
                dataCy: 'processed-filter',
            },
            {
                // the API reads the window off `start` and `end`
                name: 'range',
                label: 'Charged between',
                type: 'dateRange',
                startName: 'range_after',
                endName: 'range_before',
                dataCy: 'range-filter',
            },
        ],
        [isOwner, teamsResource, applicationsResource, serviceItemsResource, servicesResource],
    );

    const columns: TableListColumn[] = useMemo(
        () =>
            [
                { label: 'ID', dataCy: 'column-id' },
                { label: 'Service item', filters: ['service_item_id'], dataCy: 'column-service-item' },
                // the column the service owner was missing
                isOwner && {
                    label: 'Consumer',
                    filters: ['consumer_team_id'],
                    dataCy: 'column-consumer',
                },
                { label: 'Application', filters: ['application_id'], dataCy: 'column-application' },
                { label: 'Type', filters: ['charge_type'], dataCy: 'column-charge-type' },
                { label: 'Total', align: 'right' as const, dataCy: 'column-total' },
                { label: 'Processed', filters: ['processed'], dataCy: 'column-processed' },
                { label: 'Created', filters: ['range'], dataCy: 'column-created' },
                // the filter button takes the track after this one
            ].filter(Boolean) as TableListColumn[],
        [isOwner],
    );

    return (
        <NetOrcaTableListV1
            className={`netorca-chg netorca-chg--${pov}`}
            dataCy="charge-result-list"
            fields={filterFields}
            columns={columns}
            template="var(--chg-cols)"
            emptyText="No charges found"
            noun="charge"
            drawerTitle="Filter charges"
            // What the spend did and what it came to, side by side above the
            // list — both read the same filters as the rows under them. They
            // scroll away with the rows, and the header stops at the top.
            scrollBanner
            banner={
                <div className="chg-banner">
                    <section className="chg-panel chg-panel--chart" aria-label="Charges over time">
                        <ChargeChart points={points} loading={loading} />
                    </section>
                    <section className="chg-panel chg-panel--summary" aria-label="Charge summary">
                        <ChargeSummary totals={totals} loading={loading} />
                    </section>
                </div>
            }
            renderRow={(record) => (
                <ChargeRow
                    key={record.id}
                    record={record}
                    serviceItemPath={`/${serviceItemsResource}`}
                    showConsumer={isOwner}
                />
            )}
        />
    );
};

const ChargeList = (props: ListProps) => (
    // page size is the batch the infinite scroll fetches
    <ListBase sort={{ field: 'id', order: 'DESC' }} perPage={25} {...props}>
        <ChargeListView />
    </ListBase>
);

export default ChargeList;
